import React, { useRef, useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import PropTypes from "prop-types"
import swal from "sweetalert"

import { Button } from "quorumdesign"

import { getPacPaths } from "imports/desktopPaths"

const { PacFile } = DjangIO.app.pac.models.pac_file

/**
 * A ref whose .current will be false if this component is no longer mounted.
 * Useful for avoiding state updates after unmounting.
 *
 * Sample usage:
 *
 *     const mountedRef = useMountedRef()
 *     // inside an event handler or side effect
 *     if (mountedRef.current) {
 *         setState(...)
 *     }
 */
const useMountedRef = () => {
    const mountedRef = useRef(true)
    // only return a cleanup function, which fires on unmount
    useEffect(() => () => (mountedRef.current = false), [])
    return mountedRef
}

/**
 * "Amend" button for PAC filings. Disabled if fields.isAmendable is false.
 *
 * 1. Calls the `amend_pacfile` API endpoint to create an amendment, which
 *    is a PacFile object. Or to find one if it is already created and in progress.
 *
 * 2. Displays any feedback such as error messages. These should be
 *    mostly avoidable by disabling controls appropriately.
 *
 * 3. In success, navigates to the Filing Form for that amendment.
 */
const AmendPacFileAction = ({ fields, type, icon = "edit", text = "Amend" }) => {
    const [loading, setLoading] = useState(false)
    const mountedRef = useMountedRef()

    const navigate = useNavigate()

    const disabled = !fields.isAmendable || loading
    const id = fields.id
    const jurisdictionId = fields.regionId

    const handleClick = useCallback(async () => {
        const pacFile = id && PacFile.objects.get(id)
        if (pacFile && !loading) {
            const url = `${pacFile.getEndpoint()}amend_pacfile/?decode_enums=false`
            try {
                setLoading(true)
                const response = await fetch(url, {
                    method: "POST",
                    headers: { "X-CSRFToken": window.csrf_token },
                })
                const json = await response.json()
                let errorMessage = undefined
                if (response.ok) {
                    const id = json?.id
                    if (!id) {
                        // this would be a bug; haven't seen it happen
                        errorMessage = `Amendment not created: ${JSON.stringify(json)}.`
                    } else {
                        if (
                            jurisdictionId &&
                            jurisdictionId !== DjangIO.app.ledger.types.ReportingJurisdictionType.federal.value
                        ) {
                            const jurisdiction =
                                DjangIO.app.ledger.types.ReportingJurisdictionType.by_value(jurisdictionId)
                            navigate(
                                getPacPaths({
                                    ledgerSettingsId: fields.ledgerSettingsId,
                                    filingsId: id,
                                    jurisdictionKey: jurisdiction.key,
                                })._pacStateFilingsEdit,
                            )
                        } else {
                            navigate(
                                getPacPaths({
                                    ledgerSettingsId: fields.ledgerSettingsId,
                                    filingsId: id,
                                })._pacFilingsDetail,
                            )
                        }
                    }
                } else {
                    errorMessage = json?.message || json?.error
                }
                if (errorMessage) {
                    await swal({
                        title: "Error",
                        text: `Unable to create amendment: ${errorMessage}`,
                        icon: "error",
                    })
                }
            } finally {
                // avoid changing state after unmounted
                if (mountedRef.current) setLoading(false)
            }
        }
    }, [id])

    return (
        <Button
            type={type}
            leftIcon={loading ? "spinner" : icon}
            leftIconSpin={loading}
            text={text}
            disabled={disabled}
            onClick={handleClick}
            title={disabled ? "Cannot amend a filing that is still in progress." : undefined}
        />
    )
}

AmendPacFileAction.propTypes = {
    type: PropTypes.string,
    icon: PropTypes.string,
    text: PropTypes.string,
    fields: PropTypes.object.isRequired,
}

export default AmendPacFileAction
